import { getLineDisplay, getTotalDisplay } from "./DisplayService";
import { SPORTS_LIST } from "../bet-config";

export interface BetQLLine {
  [key: string]: any;
  awaySpread: number | null;
  homeSpread: number | null;
  awayMoney: number | null;
  homeMoney: number | null;
  awayPrice: number | null;
  homePrice: number | null;
  drawMoney: number | null;
  total: number | null;
  overPrice: number | null;
  underPrice: number | null;
  period: string | null;
  time: string | null;
  lineType: string | null;
}

export const getOpenCurrentLines = (game: any, sport: string, currentBookId?: string) => {
  const currentSportObj = SPORTS_LIST[sport];
  let openCurrentLines: any = {};
  let periods: any = {};
  let emptyLine: BetQLLine = {
    bookId: null,
    awaySpread: null,
    homeSpread: null,
    awayMoney: null,
    homeMoney: null,
    awayPrice: null,
    homePrice: null,
    drawMoney: null,
    total: null,
    overPrice: null,
    underPrice: null,
    period: null,
    time: null,
    lineType: null,
  };
  currentSportObj.periodKeys.forEach((period: string) => {
    periods[period] = {
      open: { ...emptyLine, period, lineType: "open" },
      current: { ...emptyLine, period, lineType: "current" },
    };
    openCurrentLines[`open${period}Line`] = {
      ...emptyLine,
      period,
      lineType: "open",
    };
    openCurrentLines[`current${period}Line`] = {
      ...emptyLine,
      period,
      lineType: "current",
    };
  });
  /**
   * For every line we prefer to use consensus to determine the current and open lines
   * and if we cannot find a consensus line for that specific game we will use the next
   * line that is returned but only if the line for that period is not yet set.
   */
  if (game.lines && game.lines.length) {
    game.lines.forEach((line: BetQLLine) => {
      if (
        (currentBookId && line.bookId === currentBookId && (line.lineType === "open" || line.lineType === "current")) ||
        (!currentBookId && (line.lineType === "open" || line.lineType === "current"))
      ) {
        openCurrentLines[`${line.lineType}${line.period}Line`] = line;
        if (line.period) {
          if (periods[line.period]) {
            periods[line.period][line.lineType] = line;
          } else {
            periods[line.period] = {};
            periods[line.period][line.lineType] = line;
          }
        }
      }
    });
  }
  currentSportObj.periodKeys.forEach((period: string) => {
    openCurrentLines = {
      ...openCurrentLines,
      ...getRecentMoveValues(periods[period].open, periods[period].current, period),
    };
  });
  return openCurrentLines;
};

const getRecentMoveValues = (openLine: BetQLLine, currentLine: BetQLLine, period: string) => {
  let retValue: any = {};
  retValue[`movement${period}moneylineDisplay`] = "--";
  retValue[`movement${period}spreadDisplay`] = "--";
  retValue[`movement${period}totalDisplay`] = "--";
  retValue[`movement${period}moneylineData`] = null;
  retValue[`movement${period}spreadData`] = null;
  retValue[`movement${period}totalData`] = null;

  retValue[`movement${period}moneylineAwayDifference`] = "--";
  retValue[`movement${period}moneylineHomeDifference`] = "--";
  retValue[`movement${period}moneylineDrawDifference`] = "--";

  retValue[`movement${period}spreadAwayDifference`] = "--";
  retValue[`movement${period}spreadHomeDifference`] = "--";
  retValue[`movement${period}spreadDrawDifference`] = "--";

  retValue[`movement${period}totalAwayDifference`] = "--";
  retValue[`movement${period}totalHomeDifference`] = "--";
  retValue[`movement${period}totalDrawDifference`] = "--";

  // open/current spread data
  retValue[`open${period}spreadAwayData`] = openLine.awaySpread;
  retValue[`open${period}spreadHomeData`] = openLine.homeSpread;
  retValue[`current${period}spreadAwayData`] = currentLine.awaySpread;
  retValue[`current${period}spreadHomeData`] = currentLine.homeSpread;
  // open/current spread display
  retValue[`open${period}spreadAwayDisplay`] = getLineDisplay(openLine.awaySpread);
  retValue[`open${period}spreadHomeDisplay`] = getLineDisplay(openLine.homeSpread);
  retValue[`current${period}spreadAwayDisplay`] = getLineDisplay(currentLine.awaySpread);
  retValue[`current${period}spreadHomeDisplay`] = getLineDisplay(currentLine.homeSpread);
  // open/current spread price data
  retValue[`open${period}AwayPriceData`] = openLine.awayPrice;
  retValue[`open${period}HomePriceData`] = openLine.homePrice;
  retValue[`current${period}AwayPriceData`] = currentLine.awayPrice;
  retValue[`current${period}HomePriceData`] = currentLine.homePrice;
  // open/current spread price display
  retValue[`open${period}AwayPriceDisplay`] = getLineDisplay(openLine.awayPrice);
  retValue[`open${period}HomePriceDisplay`] = getLineDisplay(openLine.homePrice);
  retValue[`current${period}AwayPriceDisplay`] = getLineDisplay(currentLine.awayPrice);
  retValue[`current${period}HomePriceDisplay`] = getLineDisplay(currentLine.homePrice);
  // open/current moneyline data
  retValue[`open${period}moneylineAwayData`] = openLine.awayMoney;
  retValue[`open${period}moneylineHomeData`] = openLine.homeMoney;
  retValue[`open${period}moneylineDrawData`] = openLine.drawMoney;
  retValue[`current${period}moneylineAwayData`] = currentLine.awayMoney;
  retValue[`current${period}moneylineHomeData`] = currentLine.homeMoney;
  retValue[`current${period}moneylineDrawData`] = currentLine.drawMoney;
  // open/current moneyline display
  retValue[`open${period}moneylineAwayDisplay`] = getLineDisplay(openLine.awayMoney);
  retValue[`open${period}moneylineHomeDisplay`] = getLineDisplay(openLine.homeMoney);
  retValue[`open${period}moneylineDrawDisplay`] = getLineDisplay(openLine.drawMoney);
  retValue[`current${period}moneylineAwayDisplay`] = getLineDisplay(currentLine.awayMoney);
  retValue[`current${period}moneylineHomeDisplay`] = getLineDisplay(currentLine.homeMoney);
  retValue[`current${period}moneylineDrawDisplay`] = getLineDisplay(currentLine.drawMoney);
  // open/current total data
  retValue[`open${period}totalAwayData`] = openLine.total;
  retValue[`open${period}totalHomeData`] = null;
  retValue[`current${period}totalAwayData`] = currentLine.total;
  retValue[`current${period}totalHomeData`] = null;
  retValue[`open${period}OverPriceData`] = openLine.overPrice;
  retValue[`open${period}UnderPriceData`] = openLine.underPrice;
  retValue[`current${period}OverPriceData`] = currentLine.overPrice;
  retValue[`current${period}UnderPriceData`] = currentLine.underPrice;
  // open/current total display
  retValue[`open${period}totalAwayDisplay`] = getTotalDisplay(openLine.total);
  retValue[`open${period}totalHomeDisplay`] = "--";
  retValue[`current${period}totalAwayDisplay`] = getTotalDisplay(currentLine.total);
  retValue[`current${period}totalHomeDisplay`] = getTotalDisplay(currentLine.total);
  retValue[`open${period}OverPriceDisplay`] = getLineDisplay(openLine.overPrice);
  retValue[`open${period}UnderPriceDisplay`] = getLineDisplay(openLine.underPrice);
  retValue[`current${period}OverPriceDisplay`] = getLineDisplay(currentLine.overPrice);
  retValue[`current${period}UnderPriceDisplay`] = getLineDisplay(currentLine.underPrice);
  if (
    openLine.awayMoney !== null &&
    currentLine.awayMoney !== null &&
    openLine.homeMoney !== null &&
    currentLine.homeMoney !== null
  ) {
    const openAwayMoney = Math.abs(openLine.awayMoney);
    const currentAwayMoney = Math.abs(currentLine.awayMoney);
    const openHomeMoney = Math.abs(openLine.homeMoney);
    const currentHomeMoney = Math.abs(currentLine.homeMoney);
    // check if the favorite changed since the line opened
    const awayIsDifferent =
      (openLine.awayMoney > 0 && currentLine.awayMoney < 0) || (currentLine.awayMoney > 0 && openLine.awayMoney < 0);
    const homeIsDifferent =
      (openLine.homeMoney > 0 && currentLine.homeMoney < 0) || (currentLine.homeMoney > 0 && openLine.homeMoney < 0);
    retValue[`movement${period}moneylineAwayDifference`] = awayIsDifferent
      ? openAwayMoney - 100 + (currentAwayMoney - 100)
      : openAwayMoney > currentAwayMoney
      ? openAwayMoney - currentAwayMoney
      : currentAwayMoney - openAwayMoney;
    retValue[`movement${period}moneylineHomeDifference`] = homeIsDifferent
      ? openHomeMoney - 100 + (currentHomeMoney - 100)
      : openHomeMoney > currentHomeMoney
      ? openHomeMoney - currentHomeMoney
      : currentHomeMoney - openHomeMoney;
    if (openLine.drawMoney !== null && currentLine.drawMoney !== null) {
      const openDrawMoney = Math.abs(openLine.drawMoney);
      const currentDrawMoney = Math.abs(currentLine.drawMoney);
      const drawIsDifferent =
        (openLine.drawMoney > 0 && currentLine.drawMoney < 0) || (currentLine.drawMoney > 0 && openLine.drawMoney < 0);
      retValue[`movement${period}moneylineDrawDifference`] = drawIsDifferent
        ? openDrawMoney - 100 + (currentDrawMoney - 100)
        : openDrawMoney > currentDrawMoney
        ? openHomeMoney - currentDrawMoney
        : currentDrawMoney - openDrawMoney;
    }
    if (
      retValue[`movement${period}moneylineAwayDifference`] !== null &&
      retValue[`movement${period}moneylineHomeDifference`] !== null
    ) {
      const list = [
        retValue[`movement${period}moneylineAwayDifference`],
        retValue[`movement${period}moneylineHomeDifference`],
      ];
      if (retValue[`movement${period}moneylineDrawDifference`] !== null) {
        list.push(retValue[`movement${period}moneylineDrawDifference`]);
      }
      const index = list.indexOf(Math.max(...list));
      switch (index) {
        case 0:
          retValue[`movement${period}moneylineIsHome`] = false;
          retValue[`movement${period}moneylineData`] = retValue[`movement${period}moneylineAwayDifference`];
          break;
        case 1:
          retValue[`movement${period}moneylineIsHome`] = true;
          retValue[`movement${period}moneylineData`] = retValue[`movement${period}moneylineHomeDifference`];
          break;
        case 2:
          retValue[`movement${period}moneylineIsHome`] = null;
          retValue[`movement${period}moneylineData`] = retValue[`movement${period}moneylineDrawDifference`];
          break;
      }
      retValue[`movement${period}moneylineDisplay`] = `${retValue[`movement${period}moneylineData`]}`;
    }
  }
  if (
    openLine.awaySpread !== null &&
    currentLine.awaySpread !== null &&
    openLine.homeSpread !== null &&
    currentLine.homeSpread !== null
  ) {
    const doesAwaySpreadPassZero = openLine.awaySpread * currentLine.awaySpread < 0;
    const openAwaySpread = Math.abs(openLine.awaySpread);
    const currentAwaySpread = Math.abs(currentLine.awaySpread);
    const doesHomeSpreadPassZero = openLine.homeSpread * currentLine.homeSpread < 0;
    const openHomeSpread = Math.abs(openLine.homeSpread);
    const currentHomeSpread = Math.abs(currentLine.homeSpread);
    retValue[`movement${period}spreadAwayDifference`] = doesAwaySpreadPassZero
      ? openAwaySpread + currentAwaySpread
      : openAwaySpread > currentAwaySpread
      ? openAwaySpread - currentAwaySpread
      : currentAwaySpread - openAwaySpread;
    retValue[`movement${period}spreadHomeDifference`] = doesHomeSpreadPassZero
      ? openHomeSpread + currentHomeSpread
      : openHomeSpread > currentHomeSpread
      ? openHomeSpread - currentHomeSpread
      : currentHomeSpread - openHomeSpread;
    if (
      retValue[`movement${period}spreadAwayDifference`] !== null &&
      retValue[`movement${period}spreadHomeDifference`] !== null
    ) {
      retValue[`movement${period}spreadIsHome`] =
        retValue[`movement${period}spreadAwayDifference`] < retValue[`movement${period}spreadHomeDifference`];
      retValue[`movement${period}spreadData`] = retValue[`movement${period}spreadIsHome`]
        ? retValue[`movement${period}spreadHomeDifference`]
        : retValue[`movement${period}spreadAwayDifference`];
      retValue[`movement${period}spreadDisplay`] = `${retValue[`movement${period}spreadData`]}`;
    }
  }
  if (openLine.total !== null && currentLine.total !== null) {
    retValue[`movement${period}totalAwayDifference`] = retValue[`movement${period}totalData`] =
      openLine.total > currentLine.total ? openLine.total - currentLine.total : currentLine.total - openLine.total;
    retValue[`movement${period}totalDisplay`] = `${retValue[`movement${period}totalData`]}`;
  }
  return retValue;
};

export const getRecentMoveData = (event: any, filter: string) => {
  if (filter === "spread" || filter === "moneyline" || filter === "total") {
    return event[`movementFG${filter}Data`];
  } else {
    return event[`movement${filter}Data`];
  }
};

export const getRecentMove = (event: any, filter: string) => {
  if (filter === "spread" || filter === "moneyline" || filter === "total") {
    return event[`movementFG${filter}Display`];
  } else {
    return event[`movement${filter}Display`];
  }
};
